<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main class="flex flex-col">
        <div v-if="!cloudMode" class="px-12 py-6">
          <FormKit
            v-if="!isLicensed"
            v-model="licenseSettingsForm"
            type="form"
            @submit="handleSubmitLicenseSettings"
            @submit-invalid="globals.focusFirstError"
            :actions="false"
          >
            <div class="space-y-6">
              <div class="flex justify-between">
                <h3
                  tabindex="-1"
                  class="no-focus-outline scroll-m-20 text-2xl font-semibold tracking-tight"
                  ref="initialFocus"
                >
                  {{ t("pages.settings.sections.license.title") }}
                </h3>
                <span>
                  {{ t("pages.settings.sections.license.version", { version: serverVersion }) }}
                </span>
              </div>

              <div class="flex flex-col">
                <div class="flex space-x-4">
                  <FormKit
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="License"
                    label="License Key"
                    placeholder="1234567890"
                    type="text"
                    :autocomplete="autoComplete"
                  />

                  <button
                    class="mt-[24px] inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="submit"
                  >
                    {{ t("common.save") }}
                  </button>
                </div>
              </div>
            </div>
          </FormKit>
          <div v-else class="space-y-6">
            <div class="flex justify-between">
              <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                {{ t("pages.settings.sections.license.title") }}
              </h3>
              <span>
                {{ t("pages.settings.sections.license.version", { version: serverVersion }) }}
              </span>
            </div>

            <div class="flex flex-col items-start">
              <button @click="handleChangeIsMoreLicenseInfoOpen(true)" class="text-left">
                {{
                  t(licenseMessage, {
                    userCount,
                    userLimit,
                    days: Math.round((licenseExpiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
                  })
                }}
                <div class="font-bold">{{ t("msg.clickHereForMoreInformation") }}</div>
              </button>
            </div>
          </div>
        </div>

        <AuthenticationSettingsComponent />
        <ServerConfigurationSettingsComponent />

        <div class="flex flex-col space-y-6 border-t px-12 py-6">
          <div class="flex justify-between">
            <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
              {{ t("fields.settings.discovery") }}
            </h3>

            <a
              class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              href="https://docs.knocknoc.io/books/admin-guide/page/additional-client-ip-addresses"
              target="_blank"
              aria-label="Additional client ip address documentation (opens in new tab)"
            >
              {{ t("common.docs") }}
              <ArrowTopRightOnSquareIcon class="h-3 w-3" />
            </a>
          </div>

          <FormKit
            v-model="clientIpDiscoverySettingsForm"
            type="form"
            @submit="handleSubmitClientIpDiscoverySettings"
            @submit-invalid="globals.focusFirstError"
            :actions="false"
          >
            <FormKit name="DiscoverIP" label="Discover additional IP addresses" type="checkbox" />

            <FormKit
              :classes="{
                outer: 'max-w-full md:max-w-[50%]',
              }"
              name="DiscoverIPURL"
              label="IP Discovery API URL"
              placeholder="https://myip.knoc.io"
              type="text"
              validation="isHttpUrl"
              :validation-rules="{ isHttpUrl }"
              :validation-messages="{ isHttpUrl: 'URL must be http or https' }"
              :autocomplete="autoComplete"
            />

            <div class="flex justify-end">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="submit"
              >
                {{ t("common.save") }}
              </button>
            </div>
          </FormKit>
        </div>

        <div class="flex flex-col space-y-6 border-t px-12 py-6">
          <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
            {{ t("fields.settings.kiosk.heading") }}
          </h3>

          <FormKit
            v-model="kioskSettingsForm"
            type="form"
            @submit="handleSubmitKioskSettings"
            @submit-invalid="globals.focusFirstError"
            :actions="false"
          >
            <FormKit
              :classes="{
                outer: 'max-w-full md:max-w-[50%]',
              }"
              name="KioskSessionDuration"
              :label="t('fields.settings.kiosk.description')"
              type="number"
              number
              help="min 5, max 120"
              validation="required|min:5|max:120"
              :autocomplete="autoComplete"
            />

            <div class="flex justify-end">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="submit"
              >
                {{ t("common.save") }}
              </button>
            </div>
          </FormKit>
        </div>

        <div class="flex flex-col space-y-6 border-t px-12 py-6">
          <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
            {{ t("pages.settings.sections.branding.title") }}
          </h3>

          <div class="flex flex-col">
            <label class="formkit-label mb-1 inline-flex text-sm font-bold text-neutral-700 dark:text-neutral-300">
              {{ $t("fields.settings.accent") }}
            </label>

            <div class="flex items-center space-x-3" role="radiogroup" aria-labelledby="accent-label">
              <!-- Accent swatches -->
              <label
                :class="[
                  'relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 focus:outline-none',
                  highAccessibilityMode ? 'ring-theme-teal-hc' : 'ring-theme-teal',
                  accent == 'teal' ? 'outline' : undefined,
                ]"
              >
                <input
                  type="radio"
                  name="accent"
                  value="teal"
                  v-model="accent"
                  class="sr-only"
                  aria-label="Teal accent color"
                />
                <span
                  class="h-8 w-8 rounded-full border border-black border-opacity-10 bg-[#36b59c]"
                  :class="{ 'bg-theme-teal': !highAccessibilityMode, 'bg-theme-teal-hc': highAccessibilityMode }"
                ></span>
              </label>

              <label
                :class="[
                  'relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 focus:outline-none',
                  highAccessibilityMode ? 'ring-theme-grape-hc' : 'ring-theme-grape',
                  accent == 'grape' ? 'outline' : undefined,
                ]"
              >
                <input
                  type="radio"
                  name="accent"
                  value="grape"
                  v-model="accent"
                  class="sr-only"
                  aria-label="Grape accent color"
                />
                <span
                  :class="[
                    'h-8 w-8 rounded-full border border-black border-opacity-10',
                    highAccessibilityMode ? 'bg-theme-grape-hc' : 'bg-theme-grape',
                  ]"
                ></span>
              </label>

              <label
                :class="[
                  'relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 focus:outline-none',
                  highAccessibilityMode ? 'ring-theme-iceberg-hc' : 'ring-theme-iceberg',
                  accent == 'iceberg' ? 'outline' : undefined,
                ]"
              >
                <input
                  type="radio"
                  name="accent"
                  value="iceberg"
                  v-model="accent"
                  class="sr-only"
                  aria-label="Iceberg accent color"
                />
                <span
                  :class="[
                    'h-8 w-8 rounded-full border border-black border-opacity-10',
                    highAccessibilityMode ? 'bg-theme-iceberg-hc' : 'bg-theme-iceberg',
                  ]"
                ></span>
              </label>

              <label
                :class="[
                  'relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 focus:outline-none',
                  highAccessibilityMode ? 'ring-theme-rose-hc' : 'ring-theme-rose',
                  accent == 'rose' ? 'outline' : undefined,
                ]"
              >
                <input
                  type="radio"
                  name="accent"
                  value="rose"
                  v-model="accent"
                  class="sr-only"
                  aria-label="Rose accent color"
                />
                <span
                  :class="[
                    'h-8 w-8 rounded-full border border-black border-opacity-10',
                    highAccessibilityMode ? 'bg-theme-rose-hc' : 'bg-theme-rose',
                  ]"
                ></span>
              </label>

              <label
                :class="[
                  'relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 focus:outline-none',
                  highAccessibilityMode ? 'ring-theme-offsite-hc' : 'ring-theme-offsite',
                  accent == 'offsite' ? 'outline' : undefined,
                ]"
              >
                <input
                  type="radio"
                  name="accent"
                  value="offsite"
                  v-model="accent"
                  class="sr-only"
                  aria-label="Offsite accent color"
                />
                <span
                  :class="[
                    'h-8 w-8 rounded-full border border-black border-opacity-10',
                    highAccessibilityMode ? 'bg-theme-offsite-hc' : 'bg-theme-offsite',
                  ]"
                ></span>
              </label>
            </div>

            <div class="mt-4">
              <Tooltip
                aria-label="High accessibility mode"
                text="Changes the accent colors to be slightly higher contrast. Also adds some extra accessibility options to the user interface, Eg. A 'Hide Timer' button."
                :follow-cursor="true"
              >
                <FormKit
                  v-model="highAccessibilityMode"
                  name="HighAccessibilityMode"
                  label="Enable high accessibility mode"
                  type="checkbox"
                />
              </Tooltip>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div class="flex flex-col">
              <label class="formkit-label mb-1 inline-flex text-sm font-bold text-neutral-700 dark:text-neutral-300">
                {{ $t("pages.settings.forms.settings.fields.Logo.label") }}
              </label>
              <div
                class="mt-2 flex justify-center rounded-lg border border-dashed border-white/25 px-6 py-10"
                @dragover="handleDragOver"
                @dragleave="handleDragLeave"
                @drop="handleDarkModeDrop"
              >
                <div v-if="!darkModeLogoData" class="text-center">
                  <PhotoIcon class="mx-auto h-12 w-12" />
                  <div class="mt-4 flex text-sm leading-6 text-[#ccd1c4]">
                    <label
                      for="file-upload"
                      class="relative cursor-pointer rounded-md font-semibold text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-[#e05454] focus-within:ring-offset-2 focus-within:ring-offset-gray-900 hover:text-[#e05454]"
                    >
                      <span>{{ $t("fields.settings.uploadFile") }}</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        class="sr-only"
                        @change="handleDarkModeUploadLogo"
                        accept="image/png, image/jpeg"
                      />
                    </label>
                    <p class="pl-1">{{ $t("fields.settings.dragAndDrop") }}</p>
                  </div>
                  <p class="text-xs leading-5 text-[#ccd1c4]">PNG, JPG, JPEG</p>
                </div>
                <div v-else class="text-center">
                  <span v-if="darkModeLogoFile">{{ darkModeLogoFile.name }}</span>
                  <p class="cursor-pointer text-sm font-medium text-[#ccd1c4]" @click="handleRemoveDarkModeLogo">
                    {{ $t("msg.removeFile") }}
                  </p>
                </div>
              </div>
            </div>

            <div class="flex flex-col">
              <label class="formkit-label mb-1 inline-flex text-sm font-bold text-neutral-700 dark:text-neutral-300">
                {{ $t("pages.settings.forms.settings.fields.LightModeLogo.label") }}
              </label>
              <div
                class="mt-2 flex justify-center rounded-lg border border-dashed border-white/25 px-6 py-10"
                @dragover="handleDragOver"
                @dragleave="handleDragLeave"
                @drop="handleLightModeDrop"
              >
                <div v-if="!lightModeLogoData" class="text-center">
                  <PhotoIcon class="mx-auto h-12 w-12" />
                  <div class="mt-4 flex text-sm leading-6 text-[#ccd1c4]">
                    <label
                      for="file-upload"
                      class="relative cursor-pointer rounded-md font-semibold text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-[#e05454] focus-within:ring-offset-2 focus-within:ring-offset-gray-900 hover:text-[#e05454]"
                    >
                      <span>{{ $t("fields.settings.uploadFile") }}</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        class="sr-only"
                        @change="handleLightModeUploadLogo"
                        accept="image/png, image/jpeg"
                      />
                    </label>
                    <p class="pl-1">{{ $t("fields.settings.dragAndDrop") }}</p>
                  </div>
                  <p class="text-xs leading-5 text-[#ccd1c4]">PNG, JPG, JPEG</p>
                </div>
                <div v-else class="text-center">
                  <span v-if="lightModeLogoFile">{{ lightModeLogoFile.name }}</span>
                  <p class="cursor-pointer text-sm font-medium text-[#ccd1c4]" @click="handleRemoveLightModeLogo">
                    {{ $t("msg.removeFile") }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <button
              class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
              type="button"
              @click="handleSubmitBrandingSettings"
            >
              {{ t("common.save") }}
            </button>
          </div>
        </div>

        <div class="flex flex-col space-y-6 border-t px-12 py-6">
          <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
            {{ t("pages.settings.sections.ui.title") }}
          </h3>

          <div class="inline-flex items-center gap-2">
            <Switch
              v-model="newUserDashboard"
              :class="
                twMerge(
                  'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
                  newUserDashboard ? 'bg-primary' : 'bg-input',
                )
              "
            >
              <span
                :class="
                  twMerge(
                    'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform',
                    newUserDashboard ? 'translate-x-5' : 'translate-x-0',
                  )
                "
              />
            </Switch>

            <label class="font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {{ t("pages.settings.forms.settings.fields.NewUserDashboard.label") }}
            </label>
          </div>

          <div class="flex justify-end">
            <button
              class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
              type="button"
              @click="handleSubmitBrandingSettings"
            >
              {{ t("common.save") }}
            </button>
          </div>
        </div>
      </main>
    </div>

    <ErrorNotification
      :error="errorMessage"
      :title="t('msg.error')"
      @update:error="(input) => (errorMessage = input)"
    />

    <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
  </div>

  <Dialog :show="isMoreLicenseInfoOpen" v-if="isLicensed">
    <DialogOverlay />

    <DialogPanel>
      <p class="flex flex-row">
        <span>
          <DialogTitle>
            {{
              t(licenseDialogTitle, {
                userLimit,
                days: Math.round((licenseExpiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
              })
            }}
          </DialogTitle>
        </span>
        <span class="ml-auto">
          <ArrowPathIcon
            @click="refreshLicense"
            class="h-8 w-8 cursor-pointer"
            :class="{ 'animate-spin': isLicenseRefreshing }"
          />
        </span>
      </p>

      <DialogDescription class="flex flex-col space-y-2 text-sm">
        <p class="inline-flex flex-col">
          <span>
            {{
              t("msg.currentYouAreUsingLicensesForXUsers", {
                userCount,
              })
            }}
            <span v-if="isUserLimitReached">
              {{ t("msg.thisIsOverTheLicenseLimit", { userLimit }) }}
            </span>
          </span>
          <span v-if="isUserLimitReached">
            {{
              t("msg.youCanContinueToCreateNewUsers", {
                userOveragePercentage,
                userLimit: userLimit + userOverage,
              })
            }}
          </span>
          <span class="text-muted-foreground"> </span>
        </p>
        <span v-if="maxMachineLicenses && machineLincenseCount">
          {{ t("msg.activatedThisLicenseOnXMachinesOfY", { count: machineLincenseCount, max: maxMachineLicenses }) }}
        </span>
        <span v-else-if="machineLincenseCount">
          {{ t("msg.activatedThisLicenseOnXMachines", { count: machineLincenseCount }) }}
        </span>
        <span>
          {{ t("msg.noteAdminUsersAreNotIncluded") }}
        </span>
        <span>
          Please contact
          <a class="font-bold underline hover:no-underline" href="mailto:support@knocknoc.io">support@knocknoc.io</a>
          {{ " " }}or visit the licensing portal
          <a class="font-bold underline hover:no-underline" href="https://licensing.knocknoc.io" target="_blank"
            >https://licensing.knocknoc.io</a
          >
          {{ " " }}to update your license count.
        </span>
        <span class="text-muted-foreground">
          {{ t("pages.settings.dialogs.license.description.yourLicenseId", { licenseId: licenseId }) }}
        </span>
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          @click="handleChangeIsMoreLicenseInfoOpen(false)"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
        >
          {{ t("common.close") }}
        </button>

        <a
          href="https://licensing.knocknoc.io"
          class="inline-flex w-full items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
          target="_blank"
        >
          {{ t("pages.settings.dialogs.license.actions.link") }} <ArrowTopRightOnSquareIcon class="ml-1 h-4 w-4" />
        </a>
      </div>
    </DialogPanel>
  </Dialog>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";

import { DialogDescription, Switch } from "@headlessui/vue";
import { ArrowTopRightOnSquareIcon, Bars3Icon } from "@heroicons/vue/24/solid";
import { ArrowPathIcon, PhotoIcon } from "@heroicons/vue/24/outline";
import { twMerge } from "tailwind-merge";
import { useI18n } from "vue-i18n";

import globals from "../../globals";
import userService from "../../services/user.service.ts";

import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../../components/ui/dialog";
import AdminHeader from "../../components/AdminHeader.vue";
import { useAdminStore } from "../../pinia/admin.ts";
import { useUserStore } from "../../pinia/user.ts";
import Tooltip from "../../components/ui/Tooltip.vue";
import AuthenticationSettingsComponent from "./Settings/AuthenticationSettingsComponent.vue";
import ServerConfigurationSettingsComponent from "./Settings/ServerConfigurationSettingsComponent.vue";

const { t } = useI18n();

const currentDate = ref(new Date());

const initialFocus = ref(null);

const adminStore = useAdminStore();
adminStore.fetchStatus();

const userStore = useUserStore();

const search = ref("");
const sidebarOpen = ref(false);

const errorMessage = ref("");
const infoMessage = ref("");

const cloudMode = computed(() => adminStore.user?.Cloud);
const serverVersion = computed(() => adminStore.user?.ServerVersion);
const userCount = computed(() => adminStore.user?.UserCount);

const settings = ref(null);

const isLicensed = ref(false);
const licenseId = ref(null);
const isLicenseRefreshing = ref(false);
const userLimit = ref(0);
const sessionLimit = ref(0);
const licenseExpiresAt = ref(null);
const maxMachineLicenses = ref(null);
const machineLincenseCount = ref(null);
const newUserDashboard = ref(false);

const isUserLimitReached = computed(() => userCount.value > userLimit.value);
const isLicenseExpired = computed(() => licenseExpiresAt.value && licenseExpiresAt.value < currentDate.value);

const userOverage = computed(() => adminStore.user?.Licensing?.UserLimitOverage);
const userOveragePercentage = computed(() => adminStore.user?.Licensing?.UserLimitOveragePercentage);

const highAccessibilityMode = ref(null);
adminStore.fetchHighAccessibility().then((res) => (highAccessibilityMode.value = res));

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const licenseMessage = computed(() => {
  if (isLicenseExpired.value) {
    return "msg.youAreUsingUserCountOfYourUserLimitExpired";
  }

  if (cloudMode.value) {
    return "msg.youAreUsingUserCountOfYourUserLimitAuto";
  }

  return "msg.youAreUsingUserCountOfYourUserLimit";
});
const licenseDialogTitle = computed(() => {
  if (isLicenseExpired.value) {
    return "msg.youAreLicensedForUserLimitUsersExpired";
  }

  if (cloudMode.value) {
    return "msg.youAreLicensedForUserLimitUsersAuto";
  }

  return "msg.youAreLicensedForUserLimitUsers";
});

const isMoreLicenseInfoOpen = ref(false);
function handleChangeIsMoreLicenseInfoOpen(value) {
  isMoreLicenseInfoOpen.value = value;
}

function isHttpUrl({ value }) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

const licenseSettingsForm = ref({});

async function handleSubmitLicenseSettings(fields) {
  try {
    await userService.updateLicenseSettings(fields);
    fetchData();
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  }

  await adminStore.fetchStatus({ force: true });
}

const clientIpDiscoverySettingsForm = ref({});

async function handleSubmitClientIpDiscoverySettings(fields) {
  await handleSubmitSettings({
    ...settings.value,
    DiscoverIP: fields.DiscoverIP,
    DiscoverIPURL: fields.DiscoverIPURL,
  });
}

const kioskSettingsForm = ref({});

async function handleSubmitKioskSettings(fields) {
  await handleSubmitSettings({
    ...settings.value,
    KioskSessionDuration: fields.KioskSessionDuration,
  });
}

async function handleSubmitSettings(s) {
  try {
    await userService.updateConfigSettings(s);
    infoMessage.value = t("msg.settingsUpdated");
    fetchData();
  } catch (error) {
    fetchData();
    errorMessage.value = globals.renderError(t, error);
  }

  await adminStore.fetchStatus({ force: true });
}

const accent = ref("");
const darkModeLogoFile = ref(null);
const darkModeLogoData = ref("");
const lightModeLogoFile = ref(null);
const lightModeLogoData = ref("");

function handleOnDarkModeLogo(file) {
  if (file.type.match("image.*")) {
    const fileReader = new FileReader();
    darkModeLogoFile.value = file;

    fileReader.onload = () => {
      let data = fileReader.result;
      darkModeLogoData.value = data.split(",")[1];
    };
    fileReader.readAsDataURL(file);
  }
}

function handleOnLightModeLogo(file) {
  if (file.type.match("image.*")) {
    const fileReader = new FileReader();
    lightModeLogoFile.value = file;

    fileReader.onload = () => {
      let data = fileReader.result;
      lightModeLogoData.value = data.split(",")[1];
    };
    fileReader.readAsDataURL(file);
  }
}

function handleDragOver(e) {
  e.preventDefault();
  e.currentTarget.classList.add("bg-white/10");
}

function handleDragLeave(e) {
  e.currentTarget.classList.remove("bg-white/10");
}

function handleDarkModeUploadLogo(e) {
  const file = e.target.files[0];
  handleOnDarkModeLogo(file);
}

function handleDarkModeDrop(e) {
  e.preventDefault();
  const file = e.dataTransfer.files[0];
  handleOnDarkModeLogo(file);
  e.currentTarget.classList.remove("bg-white/10");
}

function handleRemoveDarkModeLogo() {
  darkModeLogoFile.value = null;
  darkModeLogoData.value = "";
}

function handleLightModeUploadLogo(e) {
  const file = e.target.files[0];
  handleOnLightModeLogo(file);
}

function handleLightModeDrop(e) {
  e.preventDefault();
  const file = e.dataTransfer.files[0];
  handleOnLightModeLogo(file);
  e.currentTarget.classList.remove("bg-white/10");
}

function handleRemoveLightModeLogo() {
  lightModeLogoFile.value = null;
  lightModeLogoData.value = "";
}

async function handleSubmitBrandingSettings() {
  try {
    await userService.updateCustomizeSettings(
      JSON.stringify({
        Accent: accent.value,
        HighAccessibility: highAccessibilityMode.value,
        Logo: darkModeLogoData.value,
        LightModeLogo: lightModeLogoData.value,
        NewUserDashboard: newUserDashboard.value,
      }),
    );
    infoMessage.value = t("msg.settingsUpdated");
    fetchData();
  } catch (error) {
    fetchData();
    errorMessage.value = globals.renderError(t, error);
  }

  await adminStore.fetchStatus({ force: true });
}

function fetchData() {
  userStore.fetchSettings({ force: true }).then(
    (response) => {
      settings.value = response.data;

      licenseSettingsForm.value = {
        License: response.data.License,
      };

      clientIpDiscoverySettingsForm.value = {
        DiscoverIP: response.data.DiscoverIP,
        DiscoverIPURL: response.data.DiscoverIPURL,
      };

      accent.value = response.data.Accent;
      darkModeLogoData.value = response.data.Logo;
      lightModeLogoData.value = response.data.LightModeLogo;
      newUserDashboard.value = response.data.NewUserDashboard;

      kioskSettingsForm.value = { KioskSessionDuration: response.data.KioskSessionDuration };
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userStore.fetchLicenseStatus({ force: true }).then(
    (response) => {
      isLicensed.value = response.data.Licensed;
      licenseId.value = response.data.ID;
      userLimit.value = response.data.UserLimit;
      sessionLimit.value = response.data.SessionLimit;
      maxMachineLicenses.value = response.data.MaxMachines;
      machineLincenseCount.value = response.data.MachineCount;
      licenseExpiresAt.value = new Date(response.data.Expires);
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  adminStore.fetchHighAccessibility({ force: true });
}

async function refreshLicense() {
  if (!settings?.value?.License) {
    errorMessage.value = t("err.failedToRefreshLicenseStatus");
    return;
  }

  try {
    isLicenseRefreshing.value = true;

    await userService.refreshLicenseSettings();

    userStore.fetchLicenseStatus({ force: true }).then((response) => {
      isLicensed.value = response.data.Licensed;
      licenseId.value = response.data.ID;
      userLimit.value = response.data.UserLimit;
      sessionLimit.value = response.data.SessionLimit;
      maxMachineLicenses.value = response.data.MaxMachines;
      machineLincenseCount.value = response.data.MachineCount;
      licenseExpiresAt.value = new Date(response.data.Expires);
    });

    await adminStore.fetchStatus({ force: true });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  } finally {
    isLicenseRefreshing.value = false;
  }
}

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();
});
</script>
